import { useDebounce } from "@lyfegen-platform/react-common";
import { byAsc, byDesc, groupBy, sortBy } from "@lyfegen-platform/utils";
import { Box } from "@material-ui/core";
import { useMemo, useState } from "react";
import { useRecoilValue } from "recoil";
import { useSimulationsPersistedFilters } from "../../../components/filters/models";
import { LiteBox } from "../../../components/lite/LiteBox";
import LyfeToolbar from "../../../components/LyfeTable/LyfeToolbar";
import { MS_UNTIL_QUERY_CALL } from "../../../constants/util-constants";
import SubscriptionForModelsUpdates from "../../../hooks/subscription-for-models-updates";
import { modelsModuleString, useModelsQuery } from "../../../hooks/useModelsModule";
import { EMPTY_VALUE_PLACEHOLDER } from "../../../models/common/data";
import { addSearchTermCondition, filterStateFamily, simulationsTableHeaderActionsState } from "../../../store/lyfe-data-table";
import { GET_FILTERED_SIMULATION_COUNT, GET_SIMULATIONS, SIMULATION_SUBSCRIPTION, UPDATE_SIMULATION_FILTER } from "../../data/queries";
import { GetSimulationsQuery } from "../../data/types/queries.generated";
import { AddButton } from "../components/AddButton";
import { SimulationTable } from "../components/SimulationTable";
import { ViewSwitcher } from "../components/ViewSwitcher";

export const simulationGroupTableColumns = [
	{
		title: "simulation-id",
		field: "id",
		default: true,
	},
	{
		title: "status",
		field: "status",
		default: true,
	},
	{
		title: "short-name",
		field: "shortName",
		default: true,
	},
	{
		title: "simulator-header-indications",
		field: "indications",
		default: true,
	},
	{
		title: "simulation-business-case",
		field: "businessCase",
		default: true,
	},
	{
		title: "model",
		field: "model",
		default: true,
	},
	{
		title: "added-by",
		field: "createdByName",
		default: false,
	},
	{
		title: "last-update",
		field: "lastUpdatedOn",
		default: false,
	},
];

const viewMoreLimit = 10;

interface IGroupViewProps {
	comparisonSimulations: Array<string>;
}

export const GroupView = (props: IGroupViewProps) => {
	const title = "simulations";
	const [searchTerm, setSearchTerm] = useState<string>("*");
	const { loading: filtersLoading, filters: filtersInitialState, refetchFilters } = useSimulationsPersistedFilters();
	const filters = useRecoilValue(filterStateFamily(title));
	const debouncedSearchTerm = useDebounce(searchTerm, MS_UNTIL_QUERY_CALL);
	const actionState = useRecoilValue(simulationsTableHeaderActionsState);

	const { data, loading, error, refetch } = useModelsQuery<GetSimulationsQuery>(GET_SIMULATIONS, {
		variables: {
			where: { ...addSearchTermCondition(filters, debouncedSearchTerm) },
			options: {},
		},
		skip: !filters,
		fetchPolicy: "no-cache",
	});
	const handleRefetch = () => {
		refetch();
		refetchFilters();
	};
	const [expandedTable, setExpandedTable] = useState(-1);

	const groupedSimulations = useMemo(() => {
		const sortMethod = actionState.action === "sortAsc" ? byAsc : byDesc;
		const groups = groupBy(data?.simulations || [], (row) =>
			JSON.stringify([row.product || EMPTY_VALUE_PLACEHOLDER, row.domainName, row.counterparty || EMPTY_VALUE_PLACEHOLDER])
		);
		groups.forEach((value, key) => {
			if (actionState.column) {
				groups.set(
					key,
					sortBy(
						value,
						sortMethod((row) => (row as any)[actionState.column as string])
					)
				);
			}
		});

		return groups;
	}, [data, actionState]);

	const totalRecordsCount = data?.simulations?.length || 0;
	const filteredRecordsCount = data?.filteredSimulationCount[0]._count;

	const handleSearch = (input: string) => {
		setSearchTerm(input || "");
	};

	const handleViewMoreClick = (tableIndex: number) => {
		setExpandedTable(expandedTable === tableIndex ? -1 : tableIndex);
	};

	return (
		<>
			<SubscriptionForModelsUpdates onCompleted={handleRefetch} query={SIMULATION_SUBSCRIPTION} />
			{!filtersLoading && (
				<LyfeToolbar
					title={title}
					buttons={[<AddButton />]}
					viewButtons={[
						<Box width="100%" display="flex" justifyContent={"space-between"}>
							<ViewSwitcher numberOfSimulations={props.comparisonSimulations.length} />
						</Box>,
					]}
					filteredRecords={filteredRecordsCount || 0}
					totalRecords={totalRecordsCount || 0}
					handleSearch={handleSearch}
					filterInitialState={filtersInitialState}
					tableColumns={simulationGroupTableColumns}
					saveFilterQuery={UPDATE_SIMULATION_FILTER}
					module={modelsModuleString}
					countQuery={GET_FILTERED_SIMULATION_COUNT}
				/>
			)}
			{error && <>Error...{console.error(error)}</>}
			{Array.from(groupedSimulations).map(([key, simulations], index) => (
				<LiteBox marginBottom={3}>
					<SimulationTable
						comparisonSimulations={props.comparisonSimulations}
						data={
							data && {
								allSimulationCount: data.allSimulationCount,
								filteredSimulationCount: data.filteredSimulationCount,
								simulations,
							}
						}
						loading={loading || filtersLoading}
						headerTitle={key}
						isGroupView
						viewMoreLimit={viewMoreLimit}
						viewMoreState={expandedTable === index}
						handleViewMoreClick={() => handleViewMoreClick(index)}
					/>
				</LiteBox>
			))}
		</>
	);
};
